
import React, { useState, useEffect } from "react";
import ChatContainer from "@/components/chat/chat-container";
import ConversationHistory from "@/components/chat/conversation-history";
import { AppSidebar } from "@/components/app-sidebar";
import { SidebarProvider, SidebarInset, SidebarTrigger } from "@/components/ui/sidebar";
import { TestHarvestButton } from "@/components/test-harvest-button";
import { useToast } from "@/hooks/use-toast";
import { useIsMobile } from "@/hooks/use-mobile";

const ChatPage: React.FC = () => {
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [guidelinesAccepted, setGuidelinesAccepted] = useState(() => {
    const savedAcceptance = localStorage.getItem('guidelines-accepted');
    return savedAcceptance === 'true';
  });
  
  const [currentThreadId, setCurrentThreadId] = useState<string | null>(() => {
    return localStorage.getItem('klara_thread_id');
  });

  useEffect(() => {
    // Initial check
    const savedAcceptance = localStorage.getItem('guidelines-accepted');
    setGuidelinesAccepted(savedAcceptance === 'true');
    
    // Setup event listener for storage events
    const handleStorageChange = (event: StorageEvent) => {
      if (event.key === 'guidelines-accepted') {
        setGuidelinesAccepted(event.newValue === 'true');
      }
    };

    window.addEventListener('storage', handleStorageChange);
    
    // Setup a custom event listener for local changes
    const handleLocalChange = () => {
      const savedAcceptance = localStorage.getItem('guidelines-accepted');
      setGuidelinesAccepted(savedAcceptance === 'true');
    };
    
    window.addEventListener('guidelines-acceptance-changed', handleLocalChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('guidelines-acceptance-changed', handleLocalChange);
    };
  }, []);

  const handleSelectConversation = (threadId: string) => {
    console.log("Selected conversation with thread ID:", threadId);
    setCurrentThreadId(threadId);
    localStorage.setItem('klara_thread_id', threadId);
  };

  const handleCreateNewConversation = () => {
    console.log("Creating new conversation");
    setCurrentThreadId(null);
    localStorage.removeItem('klara_thread_id');
    
    toast({
      title: "Ny samtale",
      description: "En ny samtale er opprettet.",
    });
  };

  if (!guidelinesAccepted) {
    return (
      <SidebarProvider>
        <div className="flex min-h-screen w-full">
          <AppSidebar />
          <SidebarInset>
            <div className="flex flex-col h-full">
              {/* Header with hamburger menu for mobile */}
              <div className="flex items-center justify-between p-4 border-b md:hidden">
                <SidebarTrigger className="p-2 h-10 w-10" />
                <h1 className="text-lg font-semibold">Klara</h1>
                <div className="w-10"></div> {/* Spacer for centering */}
              </div>
              
              <div className="flex-1 flex items-center justify-center p-4">
                <div className="text-center max-w-md mx-auto">
                  <h2 className="text-xl font-semibold mb-4">Retningslinjer må godkjennes</h2>
                  <p className="text-muted-foreground mb-4">
                    Du må lese og godkjenne retningslinjene før du kan bruke chat-funksjonen.
                    {isMobile ? (
                      <span className="block mt-2 text-sm">
                        Trykk på meny-knappen øverst til venstre og velg "Retningslinjer" for å lese og godkjenne.
                      </span>
                    ) : (
                      <span className="block mt-2">
                        Klikk på "Retningslinjer" i menyen til venstre for å lese og godkjenne.
                      </span>
                    )}
                  </p>
                </div>
              </div>
            </div>
          </SidebarInset>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider>
      <div className="flex min-h-screen w-full">
        <AppSidebar />
        <SidebarInset>
          <div className="flex flex-col md:flex-row p-2 md:p-4 min-w-0 h-full">
            <div className="flex flex-col md:flex-row w-full max-w-6xl mx-auto h-[calc(100vh-1rem)] md:h-[calc(100vh-2rem)] border rounded-lg shadow-sm overflow-hidden">
              <div className="flex-1 min-w-0">
                <ChatContainer 
                  threadId={currentThreadId}
                  onThreadIdChange={setCurrentThreadId}
                />
              </div>
              {!isMobile && (
                <div className="w-72 border-l bg-background flex-shrink-0">
                  <ConversationHistory 
                    currentThreadId={currentThreadId}
                    onSelectConversation={handleSelectConversation}
                    onCreateNewConversation={handleCreateNewConversation}
                  />
                </div>
              )}
            </div>
          </div>
        </SidebarInset>
      </div>
    </SidebarProvider>
  );
};

export default ChatPage;
